import { Directive, ElementRef, inject, Input } from '@angular/core';
import {
  BreakpointObserver,
  Breakpoints,
  BreakpointState,
} from '@angular/cdk/layout';
import { combineLatest, debounceTime, Observable, tap } from 'rxjs';
import { ComponentStore } from '@ngrx/component-store';

interface ResponsiveClassesDirectiveState {
  classNames: string[] | undefined;
}

function removeHostFromClasses(classes: string[]) {
  for (let i = 0; i < classes.length; i++) {
    if (classes[i].startsWith('host-')) {
      // Extract the value after 'host-'
      const replacement = classes[i].substring('host-'.length);

      // Replace the current element in the array
      classes[i] = replacement;
    }
  }
  return classes;
}
@Directive({
  selector: '[nxMassaudResponsiveClasses]',
  standalone: true,
})
export class ResponsiveClassesDirective extends ComponentStore<ResponsiveClassesDirectiveState> {
  private breakpointObserver = inject(BreakpointObserver);
  private el: ElementRef<HTMLElement> = inject(ElementRef<HTMLElement>);

  private readonly classNames$ = this.select((s) => s.classNames);
  private readonly breakPoint$ = this.breakpointObserver.observe([
    Breakpoints.XSmall,
    Breakpoints.Small,
    Breakpoints.Medium,
    Breakpoints.Large,
    Breakpoints.XLarge,
  ]);

  private readonly breakpointClassNamesCombined$ = combineLatest([
    this.breakPoint$,
    this.classNames$,
  ]);

  private setBreakPoint = this.effect(() => {
    return this.breakpointClassNamesCombined$.pipe(
      tap(([breakpointState, classNames]) => {
        const responsiveClasses = classNames
          ? classNames.filter((c) => c.indexOf(':') > -1)
          : [];
        let selectedBreakpoint: string | undefined;
        Object.keys(breakpointState.breakpoints).forEach((key) => {
          if (breakpointState.breakpoints[key]) {
            selectedBreakpoint = key;
          }
        });
        const fromSm = [
          Breakpoints.Small,
          Breakpoints.Medium,
          Breakpoints.Large,
          Breakpoints.XLarge,
        ];
        const addClasses: string[] = [];
        if (classNames) {
          addClasses.push(
            ...classNames.filter(
              (c) => c.indexOf(':') === -1 && c.indexOf('cmp-') === -1
            )
          );
        }
        const removeClasses: string[] = [];
        responsiveClasses.forEach((c) => {
          const classSplit = c.split(':');
          const classEncodedBreakpoint = classSplit.shift();
          const classValue = classSplit.shift();
          if (breakpointState.breakpoints[Breakpoints.XSmall]) {
            if (classEncodedBreakpoint) {
              if (classEncodedBreakpoint !== 'xs') {
                if (classValue) {
                  // this.el.nativeElement.classList.remove(classValue);
                  removeClasses.push(classValue);
                }
              } else {
                if (classValue) {
                  // this.el.nativeElement.classList.add(classValue);
                  addClasses.push(classValue);
                }
              }
            }
          } else {
            if (
              classEncodedBreakpoint === 'sm' &&
              selectedBreakpoint &&
              fromSm.indexOf(selectedBreakpoint) > -1 &&
              classValue
            ) {
              console.log('LayoutComponent apply from sm only class', {
                classValue,
              });
              // this.el.nativeElement.classList.add(classValue);
              addClasses.push(classValue);
            } else if (classEncodedBreakpoint === 'xs' && classValue) {
              // this.el.nativeElement.classList.remove(classValue);
              removeClasses.push(classValue);
            }
          }
        });
        this.el.nativeElement.classList.remove(
          ...['xs', 'sm', 'md', 'lg', 'xl'],
          ...removeHostFromClasses(removeClasses)
        );
        const breakpointHostClasses: string[] = [];
        if (breakpointState.breakpoints[Breakpoints.XSmall]) {
        } else if (breakpointState.breakpoints[Breakpoints.Small]) {
          breakpointHostClasses.push('sm');
        } else if (breakpointState.breakpoints[Breakpoints.Medium]) {
          breakpointHostClasses.push('sm', 'md');
        } else if (breakpointState.breakpoints[Breakpoints.Large]) {
          breakpointHostClasses.push('sm', 'md', 'lg');
        } else if (breakpointState.breakpoints[Breakpoints.XLarge]) {
          breakpointHostClasses.push('sm', 'md', 'lg', 'xl');
        }
        this.el.nativeElement.classList.add(
          ...breakpointHostClasses,
          ...removeHostFromClasses(addClasses)
        );
      })
    );
  });

  @Input()
  set classNames(classNames: string[]) {
    if (
      classNames.toString() !== this.get((s) => s.classNames ?? []).toString()
    ) {
      this.cleanUp();
      this.patchState({ classNames });
    }
  }

  @Input()
  set nxMassaudResponsiveClasses(classNames: string[]) {
    if (
      classNames.toString() !== this.get((s) => s.classNames ?? []).toString()
    ) {
      this.cleanUp();
      this.patchState({ classNames });
    }
  }

  constructor() {
    super({ classNames: [] });
  }

  notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
    return value !== null && value !== undefined;
  }

  private cleanUp() {
    const oldClassNames = this.get((s) => s.classNames);
    if (oldClassNames) {
      const cleanAllIncludedPrefixedOnes = oldClassNames
        .map((c) => {
          if (c.indexOf(':') > -1) {
            return c.split(':').pop();
          }
          return c;
        })
        .filter(this.notEmpty);
      this.el.nativeElement.classList.remove(...cleanAllIncludedPrefixedOnes);
    }
  }
}
